import type { PurchaseMoneyFacts, TypedMoneyBasis } from "../api/types";
import { money, percentText } from "../i18n/intl";

/**
 * G-121 S3 — the CRM's words for typed purchase money (English; the CRM's
 * pages are not yet translated — the kiosk says the same through
 * `web/src/i18n`). Every figure here is the SERVER's: this formats, it never
 * computes IVA (see `utils/typedMoneyBasis.ts`).
 */

/** The two sides of the switch, the rate named on the IVA one (OD-139). */
export function ivaBasisOptions(
  ratePercent: string,
): Array<{ value: TypedMoneyBasis; label: string }> {
  return [
    { value: "BEFORE_IVA", label: "Before IVA" },
    { value: "INCLUDES_IVA", label: `Includes IVA ${percentText(ratePercent)} %` },
  ];
}

/** Shown under a box while "Includes IVA" is picked — no arithmetic, a promise. */
export const IVA_TAKEN_OUT_HINT = "The IVA is taken out when you save.";

/** Refusal when an amount has no side picked. */
export const PICK_IVA_BASIS = "Say whether the amount is before IVA or includes IVA.";

/**
 * One line for a purchase's stored money, e.g.
 *   "€90.91 before IVA + €9.09 IVA (10 %) = €100.00"
 *   "€50.00 before IVA · IVA on the supplier's invoice"
 * Null for money of unknown basis (history before S3, or no money given) —
 * the screen then says nothing rather than guess.
 */
export function purchaseMoneyText(
  m: PurchaseMoneyFacts & { netTotalEur: string },
): string | null {
  if (m.moneyBasis === "INVOICE") {
    return `${money(m.netTotalEur)} before IVA · IVA on the supplier's invoice`;
  }
  if (
    (m.moneyBasis === "BEFORE_IVA" || m.moneyBasis === "INCLUDES_IVA") &&
    m.ivaEur !== null &&
    m.grossTotalEur !== null &&
    m.ivaRatePercent !== null
  ) {
    return `${money(m.netTotalEur)} before IVA + ${money(m.ivaEur)} IVA (${percentText(
      m.ivaRatePercent,
    )} %) = ${money(m.grossTotalEur)}`;
  }
  return null;
}

/**
 * The short note under a before-IVA figure in a table cell:
 * "+ €6.30 IVA (21 %)", "IVA on the invoice", or null when unknown.
 */
export function purchaseIvaNote(m: PurchaseMoneyFacts): string | null {
  if (m.moneyBasis === "INVOICE") return "IVA on the invoice";
  if ((m.moneyBasis === "BEFORE_IVA" || m.moneyBasis === "INCLUDES_IVA") && m.ivaEur !== null && m.ivaRatePercent !== null) {
    return `+ ${money(m.ivaEur)} IVA (${percentText(m.ivaRatePercent)} %)`;
  }
  return null;
}
